import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getDaysRemainingBeforeExpiry } from '@/db/services/offline_time_tracker';
import { OFFLINE_LIMIT_DAYS } from '@/config/env';
import { useBanner } from '@/hooks/useBanner';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { createLogger, LogLevel } from '@/lib/logger';
import { isAuthRoute, offlineLimitMessage } from './messages';

const logger = createLogger('OfflineLimitBanner', LogLevel.WARN);

/** Warn within 5% of the offline limit (1.5 days of the default 30). */
const WARN_THRESHOLD_DAYS = OFFLINE_LIMIT_DAYS * 0.05;

/** Posts the offline-limit warning when the device is close to being blocked for staying offline. */
export function useOfflineLimitBanner(): void {
  const { pathname } = useLocation();
  const { organizationId } = useCurrentPerson();
  const active = organizationId !== null && !isAuthRoute(pathname);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!active) {
      setDaysRemaining(null);
      return;
    }
    const check = async () => {
      try {
        setDaysRemaining(await getDaysRemainingBeforeExpiry());
      } catch (error) {
        logger.error('Failed to check offline limit:', error);
      }
    };
    void check();
    const interval = setInterval(check, 5 * 60 * 1000);
    // Coming online resets the timer; give the tracker a moment to record it.
    let recheck: ReturnType<typeof setTimeout> | null = null;
    const handleOnline = () => {
      setDaysRemaining(null);
      if (recheck) clearTimeout(recheck);
      recheck = setTimeout(check, 1000);
    };
    window.addEventListener('online', handleOnline);
    return () => {
      clearInterval(interval);
      if (recheck) clearTimeout(recheck);
      window.removeEventListener('online', handleOnline);
    };
  }, [active]);

  useBanner(
    active && daysRemaining !== null && daysRemaining <= WARN_THRESHOLD_DAYS
      ? offlineLimitMessage(daysRemaining)
      : null,
  );
}
